import { createContext, useContext, useState, useEffect } from "react";
import api from "../api";
import { useSearchParams } from "react-router-dom";
import { getLocalDateString } from "../util/formatDate";

const ScheduleContext = createContext(null);

export const ScheduleProvider = ({ children }) => {
    const [appointments, setAppointments] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchAllDates, setSearchAllDates] = useState(false);
    const [doctorSchedule, setDoctorSchedule] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [selectedDate, setSelectedDate] = useState(() => {
        const param = searchParams.get("date");
        if (!param) return new Date();

        const [y, m, d] = param.split("-").map(Number);
        return new Date(y, m - 1, d);
    });
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [loadingSchedule, setLoadingSchedule] = useState(false);
    const [counter, setCounter] = useState({
        appointments: 0,
        confirm: 0,
        available: 0,
    });

    const fetchAppointments = async () => {
        setLoadingAppointments(true);
        try {
            const payload = {
                doctor: selectedDoctor?.id,
                search: searchTerm || undefined,
            };
            if (!searchAllDates && selectedDate) {
                payload.date = getLocalDateString(selectedDate);
            }
            const response = await api.appointments.getMulti(
                null,
                null,
                null,
                null,
                payload
            );
            const sortedAppointments = response.sort((a, b) => {
                if (!a.time && !b.time) return 0;
                if (!a.time) return 1;
                if (!b.time) return -1;
                return a.time.localeCompare(b.time);
            });
            setAppointments(sortedAppointments);
        } finally {
            setLoadingAppointments(false);
        }
    };

    const fetchSchedule = async () => {
        if (!selectedDate || !selectedDoctor) return;
        setLoadingSchedule(true);
        try {
            const dateFormatted = getLocalDateString(selectedDate);
            const payload = {
                doctorId: selectedDoctor.id,
                date: dateFormatted,
            };
            const response = await api.availability.create(payload);
            setDoctorSchedule(response);
        } catch {
            setDoctorSchedule([]);
        } finally {
            setLoadingSchedule(false);
        }
    };

    useEffect(() => {
        if (!selectedDate) return;

        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);

            const formatted = getLocalDateString(selectedDate);
            if (newParams.get("date") === formatted) return prev;

            newParams.set("date", formatted);

            return newParams;
        });
    }, [selectedDate]);

    useEffect(() => {
        if (!searchTerm && searchAllDates) return;

        const delay = setTimeout(() => {
            fetchAppointments();
        }, 500);

        return () => clearTimeout(delay);
    }, [searchTerm, searchAllDates, selectedDoctor]);

    useEffect(() => {
        if (!searchTerm) {
            if (searchAllDates) return;
            fetchAppointments();
            if (selectedDoctor) fetchSchedule();
        }
    }, [selectedDate, selectedDoctor]);

    return (
        <ScheduleContext.Provider
            value={{
                appointments,
                doctorSchedule,
                selectedDoctor,
                setSelectedDoctor,
                selectedDate,
                setSelectedDate,
                loadingAppointments,
                loadingSchedule,
                counter,
                setCounter,
                fetchAppointments,
                fetchSchedule,
                searchTerm,
                setSearchTerm,
                searchAllDates,
                setSearchAllDates,
            }}
        >
            {children}
        </ScheduleContext.Provider>
    );
};

export const useSchedule = () => useContext(ScheduleContext);
